import React from 'react';
import { usePageContext } from 'context/hooks/usePageContext';
import { useErrorContext } from 'context/hooks/useErrorContext';
import PayWithCard from './pages/PayWithCard';
import PayWithTransfer from './pages/PayWithTransfer';
import { FC } from 'react';
import Icon from './ui/Icon/Icon';
import { formatCurrency } from 'utils';
import Success from './screens/Success';

import { ReactComponent as CreditCard } from '../assets/icons/creditcard.svg';
import { ReactComponent as Transfer } from '../assets/icons/transfer.svg';
import { ReactComponent as Cancel } from '../assets/icons/cancel.svg';
import { ReactComponent as Opay } from '../assets/icons/opay.svg';
import { PAGE_SUB, PAGES } from 'context/pageContext';
import PayWithOpay from './pages/PayWithOpay';
// TEMP: amount/fee breakdown hidden for now — re-enable to show it again
// import FeeBreakdown from './ui/FeeBreakdown';
import { usePayableAmount } from 'context/hooks/usePayableAmount';
import { methodForPage } from 'utils/feeQuote';
import ErrorComponent from './screens/Error';

export interface SwitcherProps extends React.HTMLAttributes<HTMLDivElement> {
  amount: number;
  email?: string;
  initiateTransfer?: () => void;
  isCustomsMode?: boolean;
}

const PageSwitcher: FC<SwitcherProps> = ({ amount, email, isCustomsMode }) => {
  const { page, sub, changePage } = usePageContext();
  // what we charge for the method they are on (base amount + our fee); each view sends this, and shows it
  // with the provider's fee added (displayTotal)
  const payable = usePayableAmount(methodForPage(page));
  const payableAmount = payable.totalAmount;
  const { errorBool, clearError } = useErrorContext();
  // OPay cannot carry a customs assessment, so a customs checkout that lands
  // on it (a stale page state, say) gets the error screen instead.
  const opayScreen = isCustomsMode ? <ErrorComponent /> : <PayWithOpay />;

  const handleChange = (newPage: PAGES) => {
    if (newPage === page) return;

    clearError();
    changePage(newPage);
  };

  return (
    <div>
      <div
        className={`bg-white rounded-xl w-full py-8 flex mx-auto flex-col items-center shadow-xl max-lg:hidden ${
          errorBool ? 'border-red-600 border-2' : ''
        }`}
      >
        {page === PAGES.SUCCESS ? (
          <Success amount={payableAmount} />
        ) : page === PAGES.CARD ? (
          <PayWithCard
            amount={payableAmount}
            email={email}
          />
        ) : page === PAGES.TRANSFER ? (
          <PayWithTransfer amount={payableAmount} />
        ) : page === PAGES.OPAY ? (
          opayScreen
        ) : (
          <p className="text-xl font-medium">
            Select a payment option to proceed
          </p>
        )}
      </div>

      {/* mobile */}
      <div className={'lg:hidden h-screen max-h-[100vh] bg-white space-y-4'}>
        {page === PAGES.SUCCESS && (
          <div className="h-screen w-screen p-6 flex flex-col items-center justify-center">
            <Success amount={payableAmount} />
          </div>
        )}
        <div className="flex items-center gap-2 text-[#0284F8] bg-[#C9E5FF] p-4 cursor-pointer w-full lg:hidden">
          <Icon
            svg={
              page === PAGES.CARD
                ? CreditCard
                : page === PAGES.TRANSFER
                  ? Transfer
                  : Opay
            }
            className="fill-current"
            height={page === PAGES.OPAY ? 20 : 16}
            width={page === PAGES.OPAY ? 54 : 16}
          />
          {page === PAGES.CARD
            ? 'Pay with Card'
            : page === PAGES.TRANSFER
              ? 'Pay with Transfer'
              : 'Pay with Opay'}
        </div>
        <div className="text-center p-5 bg-[#C9E5FF] rounded-sm w-full max-w-xs m-auto">
          {/* <p>You are about to pay:</p> */}
          <span className="font-bold text-2xl text-[#0284F8] pt-1">
            {formatCurrency(payable.displayTotal / 100, 'NGN')}
          </span>
          {/* TEMP: amount/fee breakdown hidden for now — re-enable to show it again
          <FeeBreakdown
            baseAmount={payable.baseAmount}
            fee={payable.displayFee}
            className="pt-2"
          /> */}
        </div>
        {page === PAGES.CARD ? (
          <PayWithCard amount={payableAmount} />
        ) : page === PAGES.TRANSFER ? (
          <PayWithTransfer amount={payableAmount} />
        ) : page === PAGES.OPAY ? (
          opayScreen
        ) : null}
        {(page === PAGES.CARD ||
          page === PAGES.TRANSFER ||
          page === PAGES.OPAY) &&
          (sub === PAGE_SUB.CARD ||
            sub === PAGE_SUB.TRANSFER ||
            sub === PAGE_SUB.OPAY) && (
            <div className="pt-[20%]">
              <button
                className="flex items-center gap-1 border border-[#f5f5f5] p-2 text-[#2E3C4E] bg-[#e4e4e4] text-sm text-center
        font-semibold rounded-md mx-auto"
                onClick={() => handleChange(PAGES.LOADING)}
                type="button"
              >
                <Icon
                  size={20}
                  svg={Cancel}
                  className="text-[#2E3C4E]"
                />
                Change Payment Method
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default PageSwitcher;
